import { BuildingOffice2Icon, UserIcon, CheckCircleIcon, XCircleIcon, DocumentCheckIcon, DocumentChartBarIcon, DocumentIcon } from "@heroicons/react/24/outline";
import Modal from "components/common/Modal";
import { Customer, CustomerFormData } from "types/customer";
import { formatDocumentDate, formatAddress } from "utils/formatters";
import { useLanguage } from "contexts/LanguageContext";
import { useAuth } from "contexts/AuthContext";
import { SimpleOrganization } from "pages/Customers";
import React, { useState, useEffect, useCallback } from 'react';
import { Tab } from '@headlessui/react';
import { useCustomers } from '../../hooks/useCustomers';
import CustomerRelationshipLogTab from './CustomerRelationshipLogTab';
import {
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  MapPinIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../common/LoadingScreen';
import PricingManagementModal from './PricingManagementModal';
import { getUserDisplayNameSync, getUserDisplayName } from '../../utils/userUtils';
import PodDetailsModal from './PodDetailsModal';
import BulkInvoicingPanel from './BulkInvoicingPanel';
import { toast } from 'react-hot-toast';
import { exportCustomerToExcel } from '../../utils/excelExport';

interface CustomerViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  organizations: SimpleOrganization[];
  onEdit: (customer: Customer) => void;
}

const CustomerViewModal: React.FC<CustomerViewModalProps> = ({
  isOpen,
  onClose,
  customer,
  organizations,
  onEdit
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { refreshCustomer, customers, updateCustomer } = useCustomers();
  const [selectedTab, setSelectedTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [localCustomer, setLocalCustomer] = useState<Customer | null>(customer);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isPodModalOpen, setIsPodModalOpen] = useState(false);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Use the most up-to-date customer data from the customers array
  useEffect(() => {
    if (customer && !hasLocalChanges) {
      // First set from the prop to ensure we have data immediately
      setLocalCustomer(customer);
      
      // Then check if we have a more recent version in the customers array
      const updatedCustomer = customers.find(c => c.id === customer.id);
      if (updatedCustomer) {
        setLocalCustomer(updatedCustomer);
      }
    } else if (!customer) {
      setLocalCustomer(null);
      setHasLocalChanges(false);
    }
  }, [customer, customers, hasLocalChanges]);
  
  // Reset tab when customer changes
  useEffect(() => {
    setSelectedTab(0);
  }, [customer?.id]);

  if (!localCustomer) {
    return null;
  }

  const formattedCustomer = localCustomer as unknown as CustomerFormData;

  const customerName = localCustomer.type === 'organization' 
    ? localCustomer.companyName 
    : `${localCustomer.firstName} ${localCustomer.lastName}`;
  
  const handleLogsUpdated = async () => {
    setRefreshKey(prev => prev + 1);
    // Frissítsük az ügyfél adatokat a Firebase-ből
    if (localCustomer) {
      try {
        const refreshedCustomer = await refreshCustomer(localCustomer.id);
        if (refreshedCustomer) {
          setLocalCustomer(refreshedCustomer);
        }
      } catch (error) {
        console.error('Error refreshing customer after logs update:', error);
      }
    }
  };

  const handlePricingUpdate = async (customerId: string) => {
    // Refresh customer data after pricing update
    try {
      const refreshedCustomer = await refreshCustomer(customerId);
      if (refreshedCustomer) {
        setLocalCustomer(refreshedCustomer);
      }
      } catch (error) {
      console.error('Error refreshing customer after pricing update:', error);
    }
  };

  const handleBulkInvoicingUpdate = async (updates: Partial<Customer>) => {
    if (!localCustomer) return;
    
    // Only update local state, don't save to database
    // User will save when clicking "Edit" button
    setLocalCustomer({
      ...localCustomer,
      ...updates
    });
    setHasLocalChanges(true);
  };

  // Helper függvény a dátum konvertáláshoz
  const getImportedAtDate = (importedAt: any): Date | null => {
    if (!importedAt) return null;
    if (importedAt instanceof Date) return importedAt;
    if (importedAt.toDate && typeof importedAt.toDate === 'function') return importedAt.toDate();
    try {
      return new Date(importedAt);
    } catch {
      return null;
    }
  };

  // Helper függvény a mérési típus formázásához
  const formatMeasurementType = (type: string): string => {
    const types: Record<string, string> = {
      'negyedoras': 'Negyedórás (15 perc)',
      'oras': 'Órás',
      'napi': 'Napi',
      'havi': 'Havi',
      'eves': 'Éves',
      'profil': 'SLP (profil alapú)'
    };
    return types[type] || type;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={customerName}
      size="7xl"
    >
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex space-x-1 rounded-t-lg bg-gray-100 p-1">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
              ${selected 
                ? 'bg-white text-primary-700 shadow' 
                : 'text-gray-700 hover:bg-white/[0.12] hover:text-gray-800'}`
            }
          >
            {t('customers.tabs.details')}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
              ${selected 
                ? 'bg-white text-primary-700 shadow' 
                : 'text-gray-700 hover:bg-white/[0.12] hover:text-gray-800'}`
            }
          >
            {t('customers.tabs.relationshipLog') || 'Relationship Log'}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
              ${selected 
                ? 'bg-white text-primary-700 shadow' 
                : 'text-gray-700 hover:bg-white/[0.12] hover:text-gray-800'}`
            }
          >
            {t('customers.tabs.history')}
          </Tab>
        </Tab.List>
        <Tab.Panels className="mt-2">
          <Tab.Panel className="rounded-xl bg-white p-3">
            <div className="p-6 space-y-6">
              {/* Header section with customer name and status */}
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl border border-primary-200">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-full ${localCustomer.type === 'organization' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                    {localCustomer.type === 'organization' ? (
                      <BuildingOffice2Icon className="h-8 w-8 text-blue-600" />
                    ) : (
                      <UserIcon className="h-8 w-8 text-purple-600" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {localCustomer.type === 'organization'
                        ? localCustomer.companyName
                        : `${localCustomer.firstName} ${localCustomer.lastName}`}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {localCustomer.type === 'organization'
                        ? t('customers.form.type.organization')
                        : t('customers.form.type.individual')}
                    </p>
                    <p className="text-xs text-gray-400 mt-1 font-mono">
                      ID: {localCustomer.id}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border-2 ${
                    localCustomer.status === 'lead' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                    localCustomer.status === 'prospect' ? 'bg-cyan-100 text-cyan-800 border-cyan-300' :
                    localCustomer.status === 'quoted' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                    localCustomer.status === 'contract' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                    localCustomer.status === 'active' ? 'bg-green-100 text-green-800 border-green-300' :
                    localCustomer.status === 'inactive' ? 'bg-gray-100 text-gray-800 border-gray-300' :
                    'bg-red-100 text-red-800 border-red-300'
                  }`}>
                    <span className="text-lg mr-2">
                      {localCustomer.status === 'lead' ? '👋' :
                       localCustomer.status === 'prospect' ? '🎯' :
                       localCustomer.status === 'quoted' ? '📋' :
                       localCustomer.status === 'contract' ? '📝' :
                       localCustomer.status === 'active' ? '✅' :
                       localCustomer.status === 'inactive' ? '⏸️' : '❌'}
                    </span>
                    {localCustomer.status === 'lead' ? 'Érdeklődő' :
                     localCustomer.status === 'prospect' ? 'Potenciális ügyfél' :
                     localCustomer.status === 'quoted' ? 'Ajánlatot kapott' :
                     localCustomer.status === 'contract' ? 'Szerződés alatt' :
                     localCustomer.status === 'active' ? 'Aktív ügyfél' :
                     localCustomer.status === 'inactive' ? 'Inaktív' : 'Elveszett'}
                  </span>
                </div>
              </div>

              {/* Data source information */}
              {(localCustomer.dataSource || localCustomer.importedAt) && (
                <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <DocumentIcon className="h-5 w-5 text-gray-600 mr-2" />
                    Adatforrás
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {localCustomer.dataSource === 'csv_import' && (
                      <span className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200">
                        📊 2025 CSV Importált
                      </span>
                    )}
                    {localCustomer.dataSource === 'csv_import_2026' && (
                      <span className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                        💰 2026 Árazás
                      </span>
                    )}
                    {localCustomer.hasFuturePricing && (
                      <span className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        🔮 Jövőbeli árazás
                      </span>
                    )}
                    {localCustomer.needsReview && (
                      <span className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                        ⚠️ Felülvizsgálat szükséges
                      </span>
                    )}
                    {localCustomer.importedAt && (
                      <span className="text-sm text-gray-600 bg-white px-3 py-2 rounded-full border border-gray-200">
                        Importálva: {(() => {
                          const date = getImportedAtDate(localCustomer.importedAt);
                          return date ? date.toLocaleDateString('hu-HU') : 'N/A';
                        })()}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Contract details card */}
              <div className="p-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-2" />
                        {t('customers.form.contractDetails.title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.contractDetails.contractNumber')}
                      </label>
                      <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-blue-200">
                        {formattedCustomer.contractNumber || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.contractDetails.taxNumber')}
                      </label>
                      <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-blue-200">
                        {formattedCustomer.taxNumber || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Közösségi adószám (EU VAT)
                      </label>
                      <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-blue-200">
                        {formattedCustomer.euVatNumber || '-'}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.contractDetails.registrationNumber')}
                      </label>
                      <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-blue-200">
                        {formattedCustomer.registrationNumber || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.contractDetails.bankAccount')}
                      </label>
                      <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-blue-200">
                        {formattedCustomer.bankAccount || '-'}
                      </p>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.contractDetails.companyAddress')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-blue-200">
                      {typeof formattedCustomer.companyAddress === 'object' && formattedCustomer.companyAddress
                        ? formatAddress(formattedCustomer.companyAddress)
                        : formattedCustomer.companyAddress || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact details card */}
              <div className="p-6 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <PhoneIcon className="h-5 w-5 text-green-600 mr-2" />
                  {t('customers.form.contactDetails.title')}
                </h3>

                {/* Képviselő */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Képviselő</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.contactDetails.representativeName')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-green-200">
                      {formattedCustomer.representativeName || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Képviselő telefonszáma
                    </label>
                    <div className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-green-200">
                      {formattedCustomer.representativePhone ? (
                        <a href={`tel:${formattedCustomer.representativePhone}`} className="text-green-600 hover:text-green-700 hover:underline flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-2" />
                          {formattedCustomer.representativePhone}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Képviselő email címe
                    </label>
                    <div className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-green-200">
                      {formattedCustomer.representativeEmail ? (
                        <a href={`mailto:${formattedCustomer.representativeEmail}`} className="text-green-600 hover:text-green-700 hover:underline flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          {formattedCustomer.representativeEmail}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                </div>

                {/* Kapcsolattartó */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Kapcsolattartó</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.contactDetails.contactName')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-green-200">
                      {localCustomer.contactPerson?.name || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.contactDetails.contactPhone')}
                    </label>
                    <div className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-green-200">
                      {localCustomer.contactPerson?.phone ? (
                        <a href={`tel:${localCustomer.contactPerson.phone}`} className="text-green-600 hover:text-green-700 hover:underline flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-2" />
                          {localCustomer.contactPerson.phone}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.contactDetails.contactEmail')}
                    </label>
                    <div className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-green-200">
                      {localCustomer.contactPerson?.email ? (
                        <a href={`mailto:${localCustomer.contactPerson.email}`} className="text-green-600 hover:text-green-700 hover:underline flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          {localCustomer.contactPerson.email}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial contact card */}
              <div className="p-6 bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl border border-amber-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-amber-600 mr-2" />
                        {t('customers.form.financialContact.title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.billingEmail')}
                    </label>
                    <div className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-amber-200">
                        {formattedCustomer.billingEmail ? (
                        <a href={`mailto:${formattedCustomer.billingEmail}`} className="text-amber-600 hover:text-amber-700 hover:underline flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                            {formattedCustomer.billingEmail}
                          </a>
                        ) : '-'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.additionalBillingEmails')}
                    </label>
                    <div className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-amber-200">
                        {formattedCustomer.additionalBillingEmails && formattedCustomer.additionalBillingEmails.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {formattedCustomer.additionalBillingEmails.map((email: string, idx: number) => (
                            <a key={idx} href={`mailto:${email}`} className="text-amber-600 hover:text-amber-700 hover:underline flex items-center">
                              <EnvelopeIcon className="h-4 w-4 mr-2" />
                              {email}
                            </a>
                          ))}
                        </div>
                        ) : '-'}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.financialContact.name')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-amber-200">
                        {formattedCustomer.financialContact?.name || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.financialContact.email')}
                    </label>
                    <div className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-amber-200">
                        {formattedCustomer.financialContact?.email ? (
                        <a href={`mailto:${formattedCustomer.financialContact.email}`} className="text-amber-600 hover:text-amber-700 hover:underline flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                            {formattedCustomer.financialContact.email}
                          </a>
                        ) : '-'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.financialContact.phone')}
                    </label>
                    <div className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-amber-200">
                        {formattedCustomer.financialContact?.phone ? (
                        <a href={`tel:${formattedCustomer.financialContact.phone}`} className="text-amber-600 hover:text-amber-700 hover:underline flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-2" />
                            {formattedCustomer.financialContact.phone}
                          </a>
                        ) : '-'}
                    </div>
                  </div>
                </div>

                {/* Aggregated all billing emails */}
                <div className="mt-4 p-4 bg-white rounded-lg border-2 border-emerald-300">
                  <label className="block text-sm font-medium text-emerald-800 mb-2">
                    📧 Számlák küldésére használt összes e-mail cím
                  </label>
                  {(() => {
                    const emails: string[] = [];
                    if (formattedCustomer.billingEmail) emails.push(formattedCustomer.billingEmail);
                    if (formattedCustomer.financialContact?.email) emails.push(formattedCustomer.financialContact.email);
                    if (formattedCustomer.additionalBillingEmails && formattedCustomer.additionalBillingEmails.length > 0) {
                      emails.push(...formattedCustomer.additionalBillingEmails);
                    }
                    const uniqueEmails = emails.filter((e, i) => emails.indexOf(e) === i);
                    return uniqueEmails.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {uniqueEmails.map((email: string, idx: number) => (
                          <span key={idx} className="inline-flex items-center px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700 font-medium">
                            <EnvelopeIcon className="h-3.5 w-3.5 mr-1.5" />
                            {email}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">-</p>
                    );
                  })()}
                </div>
              </div>

              {/* Consumption data card */}
              <div className="p-6 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ChartBarIcon className="h-5 w-5 text-purple-600 mr-2" />
                        {t('customers.form.consumptionData.title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.consumptionData.date')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-purple-200">
                        {formattedCustomer.consumptionData?.date || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.consumptionData.dueDate')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-purple-200">
                        {formattedCustomer.consumptionData?.dueDate ? `${formattedCustomer.consumptionData.dueDate} nap` : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.consumptionData.profileNumber')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-purple-200">
                        {formattedCustomer.consumptionData?.profileNumber || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.consumptionData.power')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-purple-200">
                      {formattedCustomer.consumptionData?.power ? formatNumber(formattedCustomer.consumptionData.power) : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.consumptionData.consumption')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-purple-200">
                        {formattedCustomer.consumptionData?.consumption ? formatNumber(formattedCustomer.consumptionData.consumption) : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* POD Information card */}
              {(localCustomer.pod || (localCustomer.additionalPods && localCustomer.additionalPods.length > 0)) && (
                <div className="p-6 bg-gradient-to-r from-violet-50 to-violet-100 rounded-xl border border-violet-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MapPinIcon className="h-5 w-5 text-violet-600 mr-2" />
                      POD Információk
                    </h3>
                    <button
                      onClick={() => setIsPodModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 border border-violet-300 text-sm font-medium rounded-lg text-violet-700 bg-white hover:bg-violet-50 transition-colors shadow-sm hover:shadow-md"
                    >
                      <EyeIcon className="w-4 h-4 mr-2" />
                      Részletes nézet
                    </button>
                  </div>
                  
                  {/* Summary info */}
                  <div className="space-y-3">
                    {localCustomer.pod && (
                      <div className="p-4 bg-white rounded-lg border border-violet-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-violet-700">Fő POD</span>
                          <span className="text-xs text-gray-500 bg-violet-50 px-2 py-1 rounded-full">
                            {localCustomer.pod.meteringType || localCustomer.pod.measurementType || 'N/A'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">POD ID:</span>
                            <span className="ml-2 font-medium text-gray-900 font-mono">
                              {localCustomer.pod.id || '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Éves fogyasztás:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {localCustomer.pod.annualConsumption ? `${formatNumber(localCustomer.pod.annualConsumption)} kWh` : '-'}
                            </span>
                          </div>
                          {localCustomer.pod.multiplier && (
                            <div>
                              <span className="text-gray-600">Szorzó:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {localCustomer.pod.multiplier}
                              </span>
                            </div>
                          )}
                        </div>
                        {localCustomer.pod.address && (
                          <div className="mt-2 text-sm">
                            <span className="text-gray-600">Cím (aggregált):</span>
                            <span className="ml-2 text-gray-900">{localCustomer.pod.address}</span>
                          </div>
                        )}
                        {localCustomer.pod.partnerId && (
                          <div className="mt-2 text-sm">
                            <span className="text-gray-600">Partneri azonosító:</span>
                            <span className="ml-2 text-gray-900">{localCustomer.pod.partnerId}</span>
                          </div>
                        )}
                        {(localCustomer.pod.postalCode || localCustomer.pod.city || localCustomer.pod.street) && (
                          <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                            <div className="text-xs font-medium text-gray-500 mb-1">Szétbontott cím:</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                              {localCustomer.pod.postalCode && (
                                <div>
                                  <span className="text-gray-500">Irányítószám:</span>
                                  <span className="ml-1 font-medium text-gray-900">{localCustomer.pod.postalCode}</span>
                                </div>
                              )}
                              {localCustomer.pod.city && (
                                <div>
                                  <span className="text-gray-500">Város:</span>
                                  <span className="ml-1 font-medium text-gray-900">{localCustomer.pod.city}</span>
                                </div>
                              )}
                              {localCustomer.pod.street && (
                                <div>
                                  <span className="text-gray-500">Utca:</span>
                                  <span className="ml-1 font-medium text-gray-900">{localCustomer.pod.street}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Számlázási kontroll info */}
                        {(localCustomer.pod.billable !== undefined || localCustomer.pod.suspensionReason || localCustomer.pod.billingSuspendedFrom || localCustomer.pod.billingSuspendedTo) && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="text-xs font-medium text-amber-800 mb-2">Számlázási kontroll</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                              {localCustomer.pod.billable !== undefined && (
                                <div>
                                  <span className="text-gray-600">Számlázható:</span>
                                  <span className={`ml-2 font-medium ${localCustomer.pod.billable ? 'text-green-700' : 'text-red-700'}`}>
                                    {localCustomer.pod.billable ? 'Igen' : 'Nem'}
                                  </span>
                                </div>
                              )}
                              {localCustomer.pod.suspensionReason && (
                                <div>
                                  <span className="text-gray-600">Felfüggesztés oka:</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {localCustomer.pod.suspensionReason === 'reklamacio' && 'Reklamáció'}
                                    {localCustomer.pod.suspensionReason === 'atiras' && 'Átírás'}
                                    {localCustomer.pod.suspensionReason === 'ugyfel_tavozas' && 'Ügyfél távozás'}
                                    {localCustomer.pod.suspensionReason === 'egyeb' && 'Egyéb'}
                                  </span>
                                </div>
                              )}
                              {localCustomer.pod.billingSuspendedFrom && (
                                <div>
                                  <span className="text-gray-600">Felfüggesztés kezdete:</span>
                                  <span className="ml-2 font-medium text-gray-900">{localCustomer.pod.billingSuspendedFrom}</span>
                                </div>
                              )}
                              {localCustomer.pod.billingSuspendedTo && (
                                <div>
                                  <span className="text-gray-600">Felfüggesztés vége:</span>
                                  <span className="ml-2 font-medium text-gray-900">{localCustomer.pod.billingSuspendedTo}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Mérőóra info */}
                        {(localCustomer.pod.meterSerialNumber || localCustomer.pod.meterType || localCustomer.pod.meterInstallationDate || localCustomer.pod.meterRemovalDate) && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-xs font-medium text-blue-800 mb-2">Mérőóra adatok</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                              {localCustomer.pod.meterSerialNumber && (
                                <div>
                                  <span className="text-gray-600">Gyári szám:</span>
                                  <span className="ml-2 font-medium text-gray-900 font-mono">{localCustomer.pod.meterSerialNumber}</span>
                                </div>
                              )}
                              {localCustomer.pod.meterType && (
                                <div>
                                  <span className="text-gray-600">Típus:</span>
                                  <span className="ml-2 font-medium text-gray-900">{localCustomer.pod.meterType}</span>
                                </div>
                              )}
                              {localCustomer.pod.meterInstallationDate && (
                                <div>
                                  <span className="text-gray-600">Szerelés:</span>
                                  <span className="ml-2 font-medium text-gray-900">{localCustomer.pod.meterInstallationDate}</span>
                                </div>
                              )}
                              {localCustomer.pod.meterRemovalDate && (
                                <div>
                                  <span className="text-gray-600">Leszerelés:</span>
                                  <span className="ml-2 font-medium text-gray-900">{localCustomer.pod.meterRemovalDate}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      </div>
                    )}
                    
                    {localCustomer.additionalPods && localCustomer.additionalPods.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-violet-700">További POD-ok</span>
                          <span className="text-xs text-gray-500 bg-violet-50 px-2 py-1 rounded-full">
                            {localCustomer.additionalPods.length} db
                          </span>
                        </div>
                        {localCustomer.additionalPods.map((pod, idx) => (
                          <div key={idx} className="p-4 bg-white rounded-lg border border-violet-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-violet-600">POD {idx + 2}</span>
                              <span className="text-xs text-gray-500 bg-violet-50 px-2 py-0.5 rounded-full">
                                {pod.meteringType || pod.measurementType || 'N/A'}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">POD ID:</span>
                                <span className="ml-1 font-medium text-gray-900 font-mono">{pod.id || '-'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Éves fogyasztás:</span>
                                <span className="ml-1 font-medium text-gray-900">
                                  {pod.annualConsumption ? `${formatNumber(pod.annualConsumption)} kWh` : '-'}
                                </span>
                              </div>
                            </div>
                            {pod.address && (
                              <div className="mt-2 text-sm">
                                <span className="text-gray-500">Cím (aggregált):</span>
                                <span className="ml-1 text-gray-900">{pod.address}</span>
                              </div>
                            )}
                            {pod.partnerId && (
                              <div className="mt-2 text-sm">
                                <span className="text-gray-500">Partneri azonosító:</span>
                                <span className="ml-1 text-gray-900">{pod.partnerId}</span>
                              </div>
                            )}
                            {(pod.postalCode || pod.city || pod.street) && (
                              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                                <div className="text-xs font-medium text-gray-500 mb-1">Szétbontott cím:</div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                  {pod.postalCode && (
                                    <div>
                                      <span className="text-gray-500">Irányítószám:</span>
                                      <span className="ml-1 font-medium text-gray-900">{pod.postalCode}</span>
                                    </div>
                                  )}
                                  {pod.city && (
                                    <div>
                                      <span className="text-gray-500">Város:</span>
                                      <span className="ml-1 font-medium text-gray-900">{pod.city}</span>
                                    </div>
                                  )}
                                  {pod.street && (
                                    <div>
                                      <span className="text-gray-500">Utca:</span>
                                      <span className="ml-1 font-medium text-gray-900">{pod.street}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        <div className="text-xs text-gray-500 text-right">
                          Összes éves fogyasztás: {(() => {
                            const mainConsumption = parseFloat(localCustomer.pod?.annualConsumption || '0');
                            const additionalConsumption = localCustomer.additionalPods.reduce((sum, pod) => {
                              return sum + parseFloat(pod.annualConsumption || '0');
                            }, 0);
                            const total = mainConsumption + additionalConsumption;
                            return total > 0 ? `${formatNumber(total.toString())} kWh` : 'N/A';
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bulk Invoicing Panel */}
              {(localCustomer.pod || (localCustomer.additionalPods && localCustomer.additionalPods.length > 0)) && (
                <BulkInvoicingPanel 
                  customer={localCustomer}
                  onUpdate={handleBulkInvoicingUpdate}
                />
              )}

              {/* Contract period card */}
              <div className="p-6 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-xl border border-indigo-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarIcon className="h-5 w-5 text-indigo-600 mr-2" />
                        {t('customers.form.contractPeriod.title')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.contractPeriod.startDate')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-indigo-200">
                      {formattedCustomer.contractPeriod?.startDate || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.contractPeriod.endDate')}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-indigo-200">
                      {formattedCustomer.contractPeriod?.endDate || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Ár egysége
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-indigo-200">
                      {formattedCustomer.contractPeriod?.priceUnit || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Fizetési pénznem
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-indigo-200">
                      {formattedCustomer.contractPeriod?.paymentCurrency || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t('customers.form.contractPeriod.price')}
                      {formattedCustomer.contractPeriod?.priceUnit && (
                        <span className="ml-1 text-gray-400 font-normal">({formattedCustomer.contractPeriod.priceUnit})</span>
                      )}
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-indigo-200">
                      {formattedCustomer.contractPeriod?.price ? formatNumber(formattedCustomer.contractPeriod.price) : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Ár típusa
                    </label>
                    <div className="bg-white px-3 py-2 rounded-lg border border-indigo-200">
                      {formattedCustomer.contractPeriod?.indexedPrice ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          📊 Indexált ár
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">Fix ár</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Spread ({formattedCustomer.contractPeriod?.priceUnit || 'Ft/kWh'})
                    </label>
                    <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-indigo-200">
                      {formattedCustomer.contractPeriod?.spread
                        ? `${formattedCustomer.contractPeriod.spread} ${formattedCustomer.contractPeriod.priceUnit || 'Ft/kWh'}`
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Structure - Modernized */}
              {(localCustomer.currentPricing || localCustomer.futurePricing || localCustomer.pricingStructure) && (
                <div className="p-6 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-emerald-600 mr-2" />
                      💰 Strukturált Árazási Rendszer
                    </h3>
                    <button
                      onClick={() => setIsPricingModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 border border-emerald-300 text-sm font-medium rounded-lg text-emerald-700 bg-white hover:bg-emerald-50 transition-colors shadow-sm hover:shadow-md"
                    >
                      <CurrencyDollarIcon className="w-4 h-4 mr-2" />
                      Árazás szerkesztése
                    </button>
                  </div>

                  {/* Current pricing */}
                  {localCustomer.currentPricing && (
                    <div className="mb-6 p-4 bg-white rounded-lg border border-emerald-200">
                      <div className="flex items-center mb-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                        <h4 className="text-md font-semibold text-gray-900">
                          📅 Jelenlegi Árazás ({localCustomer.currentPricing.year})
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Ár:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {localCustomer.currentPricing.price} {localCustomer.currentPricing.currency}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Érvényesség:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {localCustomer.currentPricing.validFrom} - {localCustomer.currentPricing.validTo}
                          </span>
                        </div>
                        <div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                            ✅ {localCustomer.currentPricing.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Future pricing */}
                  {localCustomer.futurePricing && localCustomer.futurePricing.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center mb-3">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                        <h4 className="text-md font-semibold text-gray-900">
                          🔮 Jövőbeli Árazások ({localCustomer.futurePricing.length} db)
                        </h4>
                      </div>
                      <div className="space-y-3">
                        {localCustomer.futurePricing.map((pricing, index) => (
                          <div key={index} className="p-4 bg-white rounded-lg border border-emerald-200">
                            <div className="mb-2">
                              <h5 className="font-medium text-gray-900">📅 {pricing.year} év ({pricing.status})</h5>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <span className="text-gray-600">Ár:</span>
                                <span className="ml-2 font-medium text-gray-900">
                                  {pricing.price} {pricing.currency}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Érvényesség:</span>
                                <span className="ml-2 font-medium text-gray-900">
                                  {pricing.validFrom} - {pricing.validTo}
                                </span>
                              </div>
                              {pricing.consumption_kwh && (
                                <div>
                                  <span className="text-gray-600">Fogyasztás:</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {pricing.consumption_kwh} kWh
                                  </span>
                                </div>
                              )}
                              {pricing.total_amount_huf && (
                                <div>
                                  <span className="text-gray-600">Összeg:</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {pricing.total_amount_huf} HUF
                                  </span>
                                </div>
                              )}
                            </div>
                            {(pricing.pod_id || pricing.imported_at) && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                  {pricing.pod_id && (
                                    <div>
                                      <span className="text-gray-600">POD ID:</span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        {pricing.pod_id}
                                      </span>
                                    </div>
                                  )}
                                  {pricing.imported_at && (
                                    <div>
                                      <span className="text-gray-600">Importálva:</span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        {new Date(pricing.imported_at).toLocaleDateString('hu-HU', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pricing structure summary */}
                  {localCustomer.pricingStructure && (
                    <div className="p-4 bg-white rounded-lg border border-emerald-200">
                      <div className="flex items-center mb-3">
                        <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                        <h4 className="text-md font-semibold text-gray-900">
                          📊 Árazási Struktúra Összefoglaló
                        </h4>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600">Utolsó frissítés:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {(() => {
                            const date = new Date(localCustomer.pricingStructure.lastPricingUpdate);
                            return date.toLocaleDateString('hu-HU', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Offer price card - Only show if no structured pricing */}
              {!localCustomer.pricingStructure && (formattedCustomer.offerPrice?.price || formattedCustomer.offerPrice?.currency) && (
                <div className="p-6 bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl border border-teal-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 text-teal-600 mr-2" />
                        {t('customers.form.offerPrice.title')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.offerPrice.price')}
                      </label>
                      <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-teal-200">
                        {formattedCustomer.offerPrice?.price ? formatNumber(formattedCustomer.offerPrice.price) : '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {t('customers.form.offerPrice.currency')}
                      </label>
                      <p className="text-base text-gray-900 bg-white px-3 py-2 rounded-lg border border-teal-200">
                        {formattedCustomer.offerPrice?.currency || '-'}
                      </p>
              </div>
                  </div>
                </div>
              )}

              {/* Organizations card */}
              <div className="p-6 bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-xl border border-cyan-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BuildingOffice2Icon className="h-5 w-5 text-cyan-600 mr-2" />
                        {t('customers.form.organizations.title')}
                </h3>
                <div className="flex flex-wrap gap-3">
                          {localCustomer.organizations && localCustomer.organizations.length > 0 ? (
                            localCustomer.organizations.map(orgId => {
                              const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
                              return org ? (
                                <div
                                  key={org.id}
                          className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white text-cyan-700 border border-cyan-200 shadow-sm hover:shadow-md transition-shadow"
                                >
                                  <BuildingOffice2Icon className="w-4 h-4 mr-2" />
                                  {org.name}
                                </div>
                              ) : null;
                            })
                          ) : (
                    <p className="text-sm text-gray-600 bg-white px-4 py-3 rounded-lg border border-cyan-200">
                      {t('customers.viewModal.noOrganizations')}
                    </p>
                          )}
                        </div>
              </div>

              {/* Notes card */}
              {localCustomer.notes && (
                <div className="p-6 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-slate-600 mr-2" />
                        {t('customers.form.organizations.notes')}
                  </h3>
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                      {localCustomer.notes}
                    </p>
              </div>
                </div>
              )}

              {/* Documents card */}
              {localCustomer.documents && localCustomer.documents.length > 0 && (
                <div className="p-6 bg-gradient-to-r from-rose-50 to-rose-100 rounded-xl border border-rose-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <DocumentIcon className="h-5 w-5 text-rose-600 mr-2" />
                        {t('customers.form.documents.title')}
                  </h3>
                  <div className="space-y-3">
                    {localCustomer.documents.map((document) => (
                      <div key={document.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-rose-200 hover:shadow-md transition-shadow">
                            <div className="flex items-center">
                          {document.type === 'contract' && <DocumentCheckIcon className="w-5 h-5 text-rose-600 mr-3" />}
                          {document.type === 'quotation' && <DocumentChartBarIcon className="w-5 h-5 text-orange-500 mr-3" />}
                          {(!document.type || document.type === 'other') && <DocumentIcon className="w-5 h-5 text-gray-500 mr-3" />}
                              <div>
                            <span className="text-sm font-medium text-gray-800 block">
                                  {document.originalName || document.name}
                                </span>
                                <span className="text-xs text-gray-500">{formatDocumentDate(document.uploadedAt)}</span>
                              </div>
                            </div>
                            <a
                              href={document.url}
                              target="_blank"
                              rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg border border-rose-200 transition-colors"
                            >
                          <EyeIcon className="w-4 h-4 mr-2" />
                              {t('customers.form.documents.view')}
                            </a>
                          </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center pt-3">
                <button
                  onClick={() => {
                    try {
                      const pods = localCustomer.pod ? [localCustomer.pod] : [];
                      exportCustomerToExcel(localCustomer, pods);
                      toast.success('Ügyfél adatok exportálva Excel fájlba');
                    } catch (error) {
                      console.error('Excel export error:', error);
                      toast.error('Hiba az Excel export során');
                    }
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm flex items-center space-x-2"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Excel Export</span>
                </button>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      onClose();
                      onEdit(localCustomer);
                    }}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-md text-sm"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          </Tab.Panel>
          <Tab.Panel className="rounded-xl bg-white p-3">
            <CustomerRelationshipLogTab 
              logs={localCustomer.relationshipLogs} 
              customerId={localCustomer.id}
              customerName={customerName}
              customer={localCustomer}
              onLogsUpdated={handleLogsUpdated}
              key={`log-tab-${refreshKey}`}
            />
          </Tab.Panel>
          <Tab.Panel className="rounded-xl bg-white p-3">
            <CustomerHistoryTab 
              customerId={localCustomer.id} 
              customerName={customerName}
              organizationsList={organizations}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Pricing Management Modal */}
      <PricingManagementModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
        customer={localCustomer}
        onUpdate={handlePricingUpdate}
      />

      {/* POD Details Modal */}
      <PodDetailsModal
        isOpen={isPodModalOpen}
        onClose={() => setIsPodModalOpen(false)}
        customer={localCustomer}
      />
    </Modal>
  );
};

// Component to display customer history
const CustomerHistoryTab: React.FC<{ 
  customerId: string;
  customerName: string;
  organizationsList: SimpleOrganization[];
}> = ({ customerId, customerName, organizationsList }) => {
  const { loadCustomerHistory, customerHistory, historyLoading } = useCustomers();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  
  // Use useCallback for the fetch function to prevent infinite loops
  const fetchHistory = useCallback(async () => {
    if (!customerId || hasAttemptedLoad) return;
    
    try {
      setLocalLoading(true);
      setError(null);
      console.log('Fetching history for customer:', customerId);
      await loadCustomerHistory(customerId);
    } catch (err) {
      console.error("Error loading history:", err);
      setError("Failed to load history data");
    } finally {
      setLocalLoading(false);
      setHasAttemptedLoad(true);
    }
  }, [customerId, hasAttemptedLoad, loadCustomerHistory]);
  
  // Only fetch history once when the component mounts
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);
  
  // Load user names for all history entries
  useEffect(() => {
    const loadUserNames = async () => {
      const history = customerHistory[customerId] || [];
      const userIds = Array.from(new Set(history.map(entry => entry.userId)));
      
      const names: Record<string, string> = {};
      for (const userId of userIds) {
        const name = await getUserDisplayName(userId, user);
        names[userId] = name;
      }
      
      setUserNames(names);
    };
    
    if (history.length > 0) {
      loadUserNames();
    }
  }, [customerId, customerHistory, user]);
  
  const history = customerHistory[customerId] || [];
  console.log('Customer history data:', { customerId, historyRecords: history.length, historyLoading, localLoading });
  
  // Use local loading state that we control instead of the global one
  if (localLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-16 px-4">
        <InformationCircleIcon className="w-12 h-12 text-red-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-600 mb-2">Error loading history</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          {error}
        </p>
      </div>
    );
  }
  
  if (history.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <InformationCircleIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">{t('customers.history.noHistory')}</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          {t('customers.history.noHistoryDetail')}
        </p>
      </div>
    );
  }
  
  return (
    <div className="max-h-[60vh] overflow-y-auto px-4">
      <div className="py-4">
        <h3 className="text-base font-medium text-gray-900 mb-4">
          {t('customers.history.title', { name: customerName })}
        </h3>
        
        <div className="space-y-6">
          {history.map((entry) => (
            <div key={entry.id} className="border-l-2 border-gray-200 pl-4 relative">
              {/* Timestamp */}
              <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-white border-2 border-gray-300"></div>
              <div className="text-sm text-gray-500 mb-1 flex items-center">
                <ClockIcon className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                {new Date(typeof entry.timestamp === 'object' && 'toDate' in entry.timestamp ? 
                  entry.timestamp.toDate() : entry.timestamp).toLocaleString('hu-HU', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
              </div>
              
              {/* Change type */}
              <div className="text-sm font-medium text-gray-800 mb-2 flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  entry.changeType === 'create' ? 'bg-green-100 text-green-800' :
                  entry.changeType === 'update' ? 'bg-blue-100 text-blue-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {entry.changeType === 'create' && t('customers.history.created')}
                  {entry.changeType === 'update' && t('customers.history.updated')}
                  {entry.changeType === 'delete' && t('customers.history.deleted')}
                </span>
                {(() => {
                  const displayName = getUserDisplayNameSync(entry.userId, user);
                  // Ne jelenítse meg, ha User-ID formátumú
                  if (!displayName.startsWith('User-')) {
                    return (
                      <span className="text-gray-600 text-xs flex items-center">
                        <UserIcon className="w-3 h-3 mr-1" />
                        {displayName}
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
              
              {/* Changed fields */}
              <div className="space-y-2 mt-2">
                {Object.entries(entry.changedFields).map(([field, change]) => {
                  // Check if this is a complex field like POD data
                  const isPodRelated = field === 'pod' || field === 'additionalPods';
                  const isLargeObject = 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                      JSON.stringify(change.oldValue).length > 200) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                      JSON.stringify(change.newValue).length > 200);
                  
                  // Check if this is a contact person field
                  const isContactData = field === 'contactPerson' || field === 'financialContact' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.name !== undefined || change.oldValue.email !== undefined || 
                      change.oldValue.phone !== undefined || change.oldValue.fax !== undefined || 
                      change.oldValue.position !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.name !== undefined || change.newValue.email !== undefined || 
                      change.newValue.phone !== undefined || change.newValue.fax !== undefined || 
                      change.newValue.position !== undefined));
                  
                  // Check if this is consumption data
                  const isConsumptionData = field === 'consumptionData' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.consumption !== undefined || change.oldValue.date !== undefined || 
                      change.oldValue.dueDate !== undefined || change.oldValue.power !== undefined || 
                      change.oldValue.profileNumber !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.consumption !== undefined || change.newValue.date !== undefined || 
                      change.newValue.dueDate !== undefined || change.newValue.power !== undefined || 
                      change.newValue.profileNumber !== undefined));
                  
                  // Check if this is organizations field
                  const isOrganizationsData = field === 'organizations';
                  
                  // Check if this is contract period field
                  const isContractPeriodData = field === 'contractPeriod' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.startDate !== undefined || change.oldValue.endDate !== undefined || 
                      change.oldValue.price !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.startDate !== undefined || change.newValue.endDate !== undefined || 
                      change.newValue.price !== undefined));
                  
                  // Check if this is offer price field
                  const isOfferPriceData = field === 'offerPrice' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.price !== undefined || change.oldValue.currency !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.price !== undefined || change.newValue.currency !== undefined));
                  
                  // Check if this is KAT data
                  const isKatData = field === 'kat' || 
                    (change.oldValue && typeof change.oldValue === 'object' && 
                     (change.oldValue.kat !== undefined || change.oldValue.text !== undefined)) ||
                    (change.newValue && typeof change.newValue === 'object' && 
                     (change.newValue.kat !== undefined || change.newValue.text !== undefined));
                  
                  // Check if this is status field
                  const isStatusField = field === 'status';
                  
                  // Check if this is type field
                  const isTypeField = field === 'type';
                  
                  return (
                    <div key={field} className={`bg-gray-50 rounded p-2 ${isPodRelated || isLargeObject ? 'overflow-x-auto' : ''}`}>
                      <div className="text-xs font-medium text-gray-700 mb-1 flex justify-between">
                        <span>{t(`customers.fields.${field}`)}</span>
                        {isPodRelated && (
                          <span className="text-purple-600 text-[10px]">POD Data</span>
                        )}
                        {isContactData && (
                          <span className="text-blue-600 text-[10px]">Contact Data</span>
                        )}
                        {isConsumptionData && (
                          <span className="text-orange-600 text-[10px]">Consumption Data</span>
                        )}
                        {isOrganizationsData && (
                          <span className="text-indigo-600 text-[10px]">Organizations</span>
                        )}
                        {isContractPeriodData && (
                          <span className="text-emerald-600 text-[10px]">Contract Period</span>
                        )}
                        {isOfferPriceData && (
                          <span className="text-amber-600 text-[10px]">Offer Price</span>
                        )}
                        {isKatData && (
                          <span className="text-fuchsia-600 text-[10px]">KÁT</span>
                        )}
                      </div>
                    
                      {entry.changeType === 'update' && (
                        <div className={`${isPodRelated || isLargeObject ? 'block' : 'grid grid-cols-2 gap-x-4 gap-y-1'}`}>
                          <div>
                            <div className="text-[10px] text-red-500">
                              {t('customers.history.oldValue')}
                            </div>
                            {isContactData ? (
                              renderContactBadges(change.oldValue)
                            ) : isConsumptionData ? (
                              renderConsumptionBadges(change.oldValue)
                            ) : isOrganizationsData ? (
                              renderOrganizationsBadges(change.oldValue, organizationsList)
                            ) : isContractPeriodData ? (
                              renderContractPeriodBadges(change.oldValue, t)
                            ) : isOfferPriceData ? (
                              renderOfferPriceBadges(change.oldValue)
                            ) : isKatData ? (
                              renderKatBadges(change.oldValue)
                            ) : isStatusField ? (
                              renderStatusBadge(change.oldValue, t)
                            ) : isTypeField ? (
                              renderTypeBadge(change.oldValue, t)
                            ) : (
                              <div className={`text-xs text-gray-600 ${isPodRelated || isLargeObject ? 'whitespace-pre-wrap' : 'break-words'}`}>
                                {formatHistoryValue(change.oldValue)}
                              </div>
                            )}
                          </div>
                          
                          {isPodRelated && <div className="border-t my-2 border-gray-200"></div>}
                          
                          <div>
                            <div className="text-[10px] text-green-500">
                              {t('customers.history.newValue')}
                            </div>
                            {isContactData ? (
                              renderContactBadges(change.newValue)
                            ) : isConsumptionData ? (
                              renderConsumptionBadges(change.newValue)
                            ) : isOrganizationsData ? (
                              renderOrganizationsBadges(change.newValue, organizationsList)
                            ) : isContractPeriodData ? (
                              renderContractPeriodBadges(change.newValue, t)
                            ) : isOfferPriceData ? (
                              renderOfferPriceBadges(change.newValue)
                            ) : isKatData ? (
                              renderKatBadges(change.newValue)
                            ) : isStatusField ? (
                              renderStatusBadge(change.newValue, t)
                            ) : isTypeField ? (
                              renderTypeBadge(change.newValue, t)
                            ) : (
                              <div className={`text-xs text-gray-800 ${isPodRelated || isLargeObject ? 'whitespace-pre-wrap' : 'break-words'}`}>
                                {formatHistoryValue(change.newValue)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    
                      {entry.changeType === 'create' && (
                        <div>
                          <div className="text-[10px] text-green-500">
                            {t('customers.history.initialValue')}
                          </div>
                          {isContactData ? (
                            renderContactBadges(change.newValue)
                          ) : isConsumptionData ? (
                            renderConsumptionBadges(change.newValue)
                          ) : isOrganizationsData ? (
                            renderOrganizationsBadges(change.newValue, organizationsList)
                          ) : isContractPeriodData ? (
                            renderContractPeriodBadges(change.newValue, t)
                          ) : isOfferPriceData ? (
                            renderOfferPriceBadges(change.newValue)
                          ) : isKatData ? (
                            renderKatBadges(change.newValue)
                          ) : isStatusField ? (
                            renderStatusBadge(change.newValue, t)
                          ) : isTypeField ? (
                            renderTypeBadge(change.newValue, t)
                          ) : (
                            <div className={`text-xs text-gray-800 ${isPodRelated || isLargeObject ? 'whitespace-pre-wrap' : 'break-words'}`}>
                              {formatHistoryValue(change.newValue)}
                            </div>
                          )}
                        </div>
                      )}
                    
                      {entry.changeType === 'delete' && (
                        <div>
                          <div className="text-[10px] text-red-500">
                            {t('customers.history.deletedValue')}
                          </div>
                          {isContactData ? (
                            renderContactBadges(change.oldValue)
                          ) : isConsumptionData ? (
                            renderConsumptionBadges(change.oldValue)
                          ) : isOrganizationsData ? (
                            renderOrganizationsBadges(change.oldValue, organizationsList)
                          ) : isContractPeriodData ? (
                            renderContractPeriodBadges(change.oldValue, t)
                          ) : isOfferPriceData ? (
                            renderOfferPriceBadges(change.oldValue)
                          ) : isKatData ? (
                            renderKatBadges(change.oldValue)
                          ) : isStatusField ? (
                            renderStatusBadge(change.oldValue, t)
                          ) : isTypeField ? (
                            renderTypeBadge(change.oldValue, t)
                          ) : (
                            <div className={`text-xs text-gray-600 ${isPodRelated || isLargeObject ? 'whitespace-pre-wrap' : 'break-words'}`}>
                              {formatHistoryValue(change.oldValue)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Fogyasztás mező formázása ezres tagolással
const formatNumber = (value: string | number): string => {
  if (!value && value !== 0) return '';
  
  // Konvertáljuk string-re, ha szám
  const stringValue = typeof value === 'number' ? value.toString() : value;
  
  // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
  const cleanValue = stringValue.replace(/[^0-9.]/g, '');
  
  // Szétválasztjuk a tizedes rész előtti és utáni részre
  const parts = cleanValue.split('.');
  
  // Formázzuk az egész részt ezres tagolással
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Összerakjuk az eredményt
  return parts.length > 1 ? parts.join('.') : parts[0];
};

// Helper function to format values for display in history
const formatHistoryValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  
  if (typeof value === 'object') {
    // Format objects nicely
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      
      // Special handling for POD arrays
      if (value[0] && (value[0].id || value[0].address)) {
        return value.map(pod => {
          const id = pod.id || '—';
          const address = pod.address || '—';
          const consumption = pod.annualConsumption ? formatNumber(pod.annualConsumption) : '0';
          return `${id.substring(0, 15)}... (${address.substring(0, 15)}... ${consumption} kWh)`;
        }).join('\n');
      }
      
      // Special handling for organization IDs
      if (typeof value[0] === 'string' && value[0].length > 10) {
        return value.map(id => `${id.substring(0, 8)}...`).join(', ');
      }
      
      return JSON.stringify(value, null, 2);
    }
    
    return JSON.stringify(value, null, 2);
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  return String(value);
};

// Helper functions for rendering badges
const renderContactBadges = (value: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span className="text-xs text-gray-600">—</span>;
  }
  
  return (
    <div className="space-y-1">
      {value.name && <div className="text-xs"><strong>Name:</strong> {value.name}</div>}
      {value.email && <div className="text-xs"><strong>Email:</strong> {value.email}</div>}
      {value.phone && <div className="text-xs"><strong>Phone:</strong> {value.phone}</div>}
      {value.fax && <div className="text-xs"><strong>Fax:</strong> {value.fax}</div>}
      {value.position && <div className="text-xs"><strong>Position:</strong> {value.position}</div>}
    </div>
  );
};

const renderConsumptionBadges = (value: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span className="text-xs text-gray-600">—</span>;
  }
  
  return (
    <div className="space-y-1">
      {value.date && <div className="text-xs"><strong>Date:</strong> {value.date}</div>}
      {value.dueDate && <div className="text-xs"><strong>Due Date:</strong> {value.dueDate} days</div>}
      {value.power && <div className="text-xs"><strong>Power:</strong> {formatNumber(value.power)}</div>}
      {value.profileNumber && <div className="text-xs"><strong>Profile:</strong> {value.profileNumber}</div>}
      {value.consumption && <div className="text-xs"><strong>Consumption:</strong> {formatNumber(value.consumption)}</div>}
    </div>
  );
};

const renderOrganizationsBadges = (value: any, organizationsList: any[]): JSX.Element => {
  if (!value || !Array.isArray(value)) {
    return <span className="text-xs text-gray-600">—</span>;
  }
  
  return (
    <div className="flex flex-wrap gap-1">
      {value.map(orgId => {
        const org = organizationsList.find(o => o.id === orgId);
        return (
          <span key={orgId} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
            {org ? org.name : `Org-${orgId.substring(0, 8)}...`}
          </span>
        );
      })}
    </div>
  );
};

const renderContractPeriodBadges = (value: any, t: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span className="text-xs text-gray-600">—</span>;
  }
  
  return (
    <div className="space-y-1">
      {value.startDate && <div className="text-xs"><strong>Start:</strong> {value.startDate}</div>}
      {value.endDate && <div className="text-xs"><strong>End:</strong> {value.endDate}</div>}
      {value.price && <div className="text-xs"><strong>Price:</strong> {formatNumber(value.price)}</div>}
    </div>
  );
};

const renderOfferPriceBadges = (value: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span className="text-xs text-gray-600">—</span>;
  }
  
  return (
    <div className="space-y-1">
      {value.price && <div className="text-xs"><strong>Price:</strong> {formatNumber(value.price)}</div>}
      {value.currency && <div className="text-xs"><strong>Currency:</strong> {value.currency}</div>}
    </div>
  );
};

const renderKatBadges = (value: any): JSX.Element => {
  if (!value || typeof value !== 'object') {
    return <span className="text-xs text-gray-600">—</span>;
  }
  
  return (
    <div className="space-y-1">
      {value.kat && <div className="text-xs"><strong>KÁT:</strong> {value.kat}</div>}
      {value.text && <div className="text-xs"><strong>Text:</strong> {value.text}</div>}
    </div>
  );
};

const renderStatusBadge = (value: any, t: any): JSX.Element => {
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
      value === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}>
      {value === 'active' ? t('customers.status.active') : t('customers.status.inactive')}
      </span>
  );
};

const renderTypeBadge = (value: any, t: any): JSX.Element => {
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      {value === 'organization' ? t('customers.form.type.organization') : t('customers.form.type.individual')}
      </span>
  );
};

export default CustomerViewModal;